import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import type { ENUM_VALUES } from '../../platform/analytics/vocabulary'
import { getTransaction, TransactionError } from './useTransaction'
import { runBulk, type BulkOutcome } from './runBulk'
import type { Txn } from './transactionWire'

export type BulkKind = (typeof ENUM_VALUES.action_kind)[number]

type Update = components['schemas']['TransactionUpdate']

/** The backend's whole-list cap on `tags`. */
const MAX_TAGS = 50

/** Q4: concurrency 4. */
const CONCURRENCY = 4

/** A row failed for a reason the user can read. The message IS the reason `runBulk` reports. */
class RowFailure extends Error {}

/** One word for what a rejection means to the person looking at the list. */
function describe(status: number): string {
  if (status === 404) return 'No longer exists'
  if (status === 422) return 'Not accepted'
  if (status === 401 || status === 403) return 'Not allowed'
  return 'Server error'
}

/**
 * The body for one row.
 *
 * **Tags REPLACE, they do not append** — `PATCH {tags}` writes the whole list — so Add tag writes the
 * row's tags back with the new one. `runBulkEdit` passes a row it has JUST re-read for `add_tag`, not
 * the caller's snapshot, so a tag added elsewhere since the row was selected survives. A
 * GET-to-PATCH window of milliseconds remains: there is no ETag and no list-level "add" to use instead.
 */
function bodyFor(kind: BulkKind, row: Txn, value: string | null): Update {
  switch (kind) {
    case 'set_category':
      return { category_id: value }
    case 'set_merchant':
      return { merchant_id: value }
    case 'assign_owner':
      return { beneficial_owner_id: value }
    case 'add_tag': {
      const tag = value ?? ''
      return { tags: row.tags.includes(tag) ? row.tags : [...row.tags, tag] }
    }
  }
}

/**
 * Pending rows cannot be edited until the bank posts them — except for their
 * category (Q5), which the user may set at any time. A merchant is not an
 * exception: EditPanel's merchant field is disabled on a pending row too.
 */
const skipsPending = (kind: BulkKind) => kind !== 'set_category'

/**
 * The context-free core of a bulk edit: skip what cannot be edited, PATCH the rest at concurrency 4,
 * report per row. No selection, no invalidation — callers own both (`useBulkActions` for the
 * Transactions screen, Tags' "New tag" for its own picked rows).
 */
export async function runBulkEdit(
  kind: BulkKind,
  rows: readonly Txn[],
  value: string | null,
  onProgress: (done: number, total: number) => void = () => undefined,
): Promise<BulkOutcome> {
  const skipped: BulkOutcome['skipped'] = skipsPending(kind)
    ? rows.filter((r) => r.isPending).map((r) => ({ id: r.id, reason: 'pending' as const }))
    : []
  const skippedIds = new Set(skipped.map((s) => s.id))
  const todo = rows.filter((r) => !skippedIds.has(r.id))

  onProgress(0, todo.length)
  const { done, failed } = await runBulk(
    todo,
    (r) => r.id,
    async (row, signal) => {
      let current = row
      if (kind === 'add_tag') {
        try {
          current = await getTransaction(row.id)
        } catch (thrown) {
          throw new RowFailure(
            thrown instanceof TransactionError && thrown.status === 404 ? 'No longer exists' : "Couldn't read its current tags",
          )
        }
        // Already carries it: nothing to write, and that is a success, not a failure.
        if (current.tags.includes(value ?? '')) return
      }
      const body = bodyFor(kind, current, value)
      if (body.tags && body.tags.length > MAX_TAGS) throw new RowFailure('Too many tags')
      let res
      try {
        // `memory_suggestion` is ignored here on purpose: plan 2026-10-16 Ticket 10.
        res = await api.PATCH('/api/v1/transactions/{transaction_id}', {
          params: { path: { transaction_id: row.id } },
          body,
          signal,
        })
      } catch {
        throw new RowFailure('Connection lost')
      }
      if (res.error !== undefined || res.data === undefined) throw new RowFailure(describe(res.response.status))
    },
    { concurrency: CONCURRENCY, onProgress },
  )
  return { done, failed, skipped }
}
